import { DismissSuggestionsDto } from '@shared/core/phone-suggestions'
import { useCallback, useRef } from 'react'
import { useMutation } from 'react-query'
import { PhoneSuggestionsService } from 'src/services/phone-suggestions.service'

export function useDismissSuggestions() {
  const mutation = useMutation((dto: DismissSuggestionsDto) => PhoneSuggestionsService.dismissSuggestions(dto))
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const pendingRequestsRef = useRef<Map<string, DismissSuggestionsDto>>(new Map())

  const debouncedMutate = useCallback(
    (dto: DismissSuggestionsDto) => {
      const { eventId, removedNumbers } = dto

      // Clear existing timeout for this eventId
      const existingTimeout = timeoutsRef.current.get(eventId)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
      }

      // Get or create the pending request for this eventId
      const existingRequest = pendingRequestsRef.current.get(eventId)
      if (existingRequest) {
        // Merge the removed numbers with existing ones
        const allRemovedNumbers = [...new Set([...existingRequest.removedNumbers, ...removedNumbers])]
        pendingRequestsRef.current.set(eventId, {
          ...existingRequest,
          removedNumbers: allRemovedNumbers,
          clientTimestamp: dto.clientTimestamp, // Use the latest timestamp
        })
      } else {
        // Store the new request
        pendingRequestsRef.current.set(eventId, dto)
      }

      // Set new timeout for this eventId
      const timeout = setTimeout(() => {
        const finalRequest = pendingRequestsRef.current.get(eventId)
        if (finalRequest) {
          mutation.mutate(finalRequest)
          pendingRequestsRef.current.delete(eventId)
        }
        timeoutsRef.current.delete(eventId)
      }, 1000)

      timeoutsRef.current.set(eventId, timeout)
    },
    [mutation],
  )

  return {
    ...mutation,
    mutate: debouncedMutate,
  }
}
